
import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import AnimatedSection from './AnimatedSection';

// Fotografije postavi u public/images/lifestyle/
const slides = [
  { id: 'judo-1', image: '/images/lifestyle/judo-1.jpg', category: 'judo' },
  { id: 'judo-2', image: '/images/lifestyle/judo-2.jpg', category: 'judo' },
  { id: 'judo-3', image: '/images/lifestyle/judo-3.jpg', category: 'judo' },
  { id: 'ski-1', image: '/images/lifestyle/ski-1.jpg', category: 'ski' },
  { id: 'ski-2', image: '/images/lifestyle/ski-2.jpg', category: 'ski' },
  { id: 'ski-3', image: '/images/lifestyle/ski-3.jpg', category: 'ski' },
  { id: 'bike-1', image: '/images/lifestyle/bike-1.jpg', category: 'bike' },
  { id: 'bike-2', image: '/images/lifestyle/bike-2.jpg', category: 'bike' },
  { id: 'bike-3', image: '/images/lifestyle/bike-3.jpg', category: 'bike' },
  { id: 'swim-1', image: '/images/lifestyle/swim-1.jpg', category: 'swim' },
  { id: 'swim-2', image: '/images/lifestyle/swim-2.jpg', category: 'swim' },
];

const translations = {
  sr: {
    eyebrow: 'IZVAN KODA',
    title: 'Život izvan ekrana',
    subtitle: 'Kod je dio moje priče. Ostatak se piše u pokretu.',
    counter: 'Fotografija',
    of: 'od',
    categories: {
      judo: {
        label: 'DŽUDO',
        title: '15 godina na tatamiju',
        text: 'Crni pojas (1. DAN), međunarodna takmičenja i godine rada. Džudo me naučio da napredak dolazi kroz disciplinu, strpljenje i upornost.'
      },
      ski: {
        label: 'SKIJANJE',
        title: 'Izvan zone komfora',
        text: 'Planine, snijeg i sloboda kretanja. Volim trenutke u kojima je jedini zadatak uživati u stazi i pogledu.'
      },
      bike: {
        label: 'OSTALE AVANTURE',
        title: 'Život je više od koda',
        text: 'Od neobičnih mjesta i optičkih iluzija do planinskih staza i karting trka. Volim istraživati, isprobavati nove stvari i stvarati uspomene s ljudima koji me okružuju.'
      },
      swim: {
       label: 'OSTALE AVANTURE',
       title: 'Život je više od koda',
       text: 'Od neobičnih mjesta i optičkih iluzija do planinskih staza i karting trka. Volim istraživati, isprobavati nove stvari i stvarati uspomene s ljudima koji me okružuju.'
      }
    }
  },
  en: {
    eyebrow: 'BEYOND THE CODE',
    title: 'Life beyond the screen',
    subtitle: 'Code is part of my story. The rest happens in motion.',
    counter: 'Photo',
    of: 'of',
    categories: {
      judo: {
        label: 'JUDO',
        title: '15 years on the tatami',
        text: 'A black belt (1st DAN), international competitions, and years of practice. Judo taught me that progress comes through discipline, patience, and persistence.'
      },
      ski: {
        label: 'SKIING',
        title: 'Beyond the comfort zone',
        text: 'Mountains, snow, and the freedom to move. Sometimes the best moments are simply about enjoying the slope and the view.'
      },
      bike: {
        label: 'BEYOND THE ORDINARY',
        title: 'More to life than code',
        text: 'From mind-bending illusions and mountain trails to go-kart racing and spontaneous adventures. I enjoy exploring new places, trying new things, and making memories with the people around me.'
      },
      swim: {
        label: 'BEYOND THE ORDINARY',
        title: 'More to life than code',
        text: 'From mind-bending illusions and mountain trails to go-kart racing and spontaneous adventures. I enjoy exploring new places, trying new things, and making memories with the people around me.'
      }
    }
  },
  de: {
    eyebrow: 'ABSEITS DES CODES',
    title: 'Leben abseits des Bildschirms',
    subtitle: 'Code ist ein Teil meiner Geschichte. Der Rest entsteht in Bewegung.',
    counter: 'Foto',
    of: 'von',
    categories: {
      judo: {
        label: 'JUDO',
        title: '15 Jahre auf der Tatami',
        text: 'Schwarzer Gürtel (1. Dan), internationale Wettkämpfe und jahrelanges Training. Judo hat mich Disziplin, Geduld und Ausdauer gelehrt.'
      },
      ski: {
        label: 'SKIFAHREN',
        title: 'Raus aus der Komfortzone',
        text: 'Berge, Schnee und Bewegungsfreiheit. Manchmal zählt nur, die Piste und die Aussicht zu genießen.'
      },
      bike: {
        label: 'WEITERE ABENTEUER',
        title: 'Das Leben ist mehr als Code',
        text: 'Von faszinierenden Illusionen und Bergpfaden bis hin zu Kartfahren und spontanen Abenteuern. Ich entdecke gerne neue Orte, probiere Neues aus und sammle Erinnerungen mit den Menschen um mich herum.'
      },
      swim: {
        label: 'WEITERE ABENTEUER',
        title: 'Das Leben ist mehr als Code',
        text: 'Von faszinierenden Illusionen und Bergpfaden bis hin zu Kartfahren und spontanen Abenteuern. Ich entdecke gerne neue Orte, probiere Neues aus und sammle Erinnerungen mit den Menschen um mich herum.'
      }
    }
  }
};

export default function BeyondTheCode() {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language || 'sr';
  const copy = translations[language.split('-')[0]] || translations.sr;

  const [index, setIndex] = useState(0);
  const [failedImages, setFailedImages] = useState({});
  const touchStart = useRef(null);
  const suppressClick = useRef(false);

  const active = slides[index];
  const info = copy.categories[active.category];

  const next = () => setIndex(i => (i + 1) % slides.length);
  const prev = () => setIndex(i => (i - 1 + slides.length) % slides.length);

  const onTouchStart = (event) => {
    const touch = event.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
    suppressClick.current = false;
  };

  const onTouchEnd = (event) => {
    if (!touchStart.current) return;

    const touch = event.changedTouches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;

    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
      suppressClick.current = true;
      if (dx < 0) next();
      else prev();
    }

    touchStart.current = null;
  };

  return (
    <section
      id="beyond-the-code"
      className="relative overflow-hidden py-16 md:py-32"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-10 md:mb-12">
            <p className="text-primary text-xs md:text-sm font-bold tracking-[0.25em] mb-3">
              {copy.eyebrow}
            </p>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {copy.title}
            </h2>

            <p className="text-gray-400 max-w-2xl mx-auto text-sm md:text-base">
              {copy.subtitle}
            </p>
          </div>
        </AnimatedSection>

        <div
          className="relative max-w-6xl mx-auto"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onClickCapture={(e) => {
            if (suppressClick.current) {
              e.preventDefault();
              e.stopPropagation();
              suppressClick.current = false;
            }
          }}
        >
          <div className="relative overflow-hidden rounded-2xl md:rounded-3xl glass-effect h-[440px] sm:h-[500px] md:h-[550px]">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={active.id}
                initial={{ opacity: 0, scale: 1.035 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{
                  duration: 0.38,
                  ease: 'easeInOut'
                }}
                className="absolute inset-0"
              >
                {!failedImages[active.id] ? (
                  <img
                    src={active.image}
                    alt={info.title}
                    className="w-full h-full object-cover"
                    draggable={false}
                    onError={() =>
                      setFailedImages(old => ({
                        ...old,
                        [active.id]: true
                      }))
                    }
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary/25 via-darker to-secondary/20 flex items-center justify-center text-gray-400 text-center px-12">
                    {active.image}
                  </div>
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />

                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-9 md:p-12 max-w-3xl">
                  <span className="inline-block px-3 py-1 rounded-full bg-primary/25 border border-primary/30 text-indigo-100 text-xs font-bold tracking-widest mb-3">
                    {info.label}
                  </span>

                  <h3 className="text-2xl md:text-4xl font-bold text-white mb-3">
                    {info.title}
                  </h3>

                  <p className="text-gray-200 text-sm md:text-base leading-relaxed">
                    {info.text}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            <button
              type="button"
              aria-label="Previous photo"
              onClick={prev}
              className="absolute z-10 left-3 md:left-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 glass-effect rounded-full flex items-center justify-center hover:bg-white/20 transition"
            >
              <FaChevronLeft />
            </button>

            <button
              type="button"
              aria-label="Next photo"
              onClick={next}
              className="absolute z-10 right-3 md:right-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 glass-effect rounded-full flex items-center justify-center hover:bg-white/20 transition"
            >
              <FaChevronRight />
            </button>
          </div>

          <div
            className="flex justify-center gap-2 mt-5"
            aria-label="Photo navigation"
          >
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                aria-label={`${copy.counter} ${i + 1}`}
                aria-current={i === index ? 'true' : undefined}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === index
                    ? 'w-7 bg-primary'
                    : 'w-2 bg-gray-600 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          <p className="text-center text-gray-500 text-sm mt-3">
            {copy.counter} {index + 1} {copy.of} {slides.length}
          </p>
        </div>
      </div>
    </section>
  );
}
